import { z } from 'zod';
import { HONEYPOT_FIELD_NAME, honeypotFieldSchema } from './honeypot';
import { attributionSchema } from './attribution';
/** Calculator input — typed common fields, forward-compatible via passthrough. */
export const calculatorInputSchema = z
    .object({
    type: z.enum(['affordability', 'max_mortgage', 'amortization']),
    propertyValue: z.number().positive().max(100_000_000),
    equity: z.number().nonnegative().max(100_000_000),
    annualIncome: z.number().positive().max(100_000_000),
    interestRatePct: z.number().min(0).max(25).optional(),
    amortizationYears: z.number().int().min(1).max(50).optional(),
    canton: z.string().length(2).optional(),
})
    .passthrough();
/** Calculator result — what the public tool computed. */
export const calculatorResultSchema = z
    .object({
    maxMortgage: z.number().nonnegative().optional(),
    affordable: z.boolean().optional(),
    loanToValuePct: z.number().min(0).max(200).optional(),
    affordabilityRatioPct: z.number().min(0).max(500).optional(),
    monthlyCost: z.number().nonnegative().optional(),
})
    .passthrough();
export const phoneSchema = z
    .string()
    .trim()
    .min(6)
    .max(32)
    .regex(/^[+0-9 ()/.-]+$/, 'invalid phone');
/**
 * Bewertungs-funnel phone gate (owner 29-8, #508): CH + DE + AT. The widget
 * (`apps/immo-map/src/telefon.ts`) mirrors this table — the map bundle may not
 * import runtime values from shared — and puts E.164 with one of these codes on
 * the wire; a national «0…» form is read for the country the visitor chose.
 * `phoneSchema` above stays the lenient wire charset (older widget bundles keep
 * sending raw spellings until Pages is redeployed); the backend's
 * `SMS_ALLOWED_PREFIXES` default is pinned to exactly these codes.
 */
export const TELEFON_LAENDER = { CH: '+41', DE: '+49', AT: '+43' };
/** E.164 of the three countries: CH a 9-digit NSN, DE/AT 6–13 (E.164 caps at 15). */
export const TELEFON_E164_RE = /^\+(?:41[1-9][0-9]{8}|49[1-9][0-9]{5,12}|43[1-9][0-9]{5,12})$/;
/** Public lead intake payload (POST /ingest/lead). */
export const leadIngestSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email().max(254),
    phone: phoneSchema.optional(),
    locale: z.enum(['de', 'fr', 'it', 'en']).default('de'),
    consent: z.object({
        privacyPolicy: z.literal(true), // must explicitly accept
        marketingEmail: z.boolean().default(false),
        /**
         * Version tag of the consent wording shown to the user (revDSG audit
         * trail) — e.g. 'finavest-consent-2026.1'. Optional for older producers;
         * the backend stores it on the consent_records rows.
         */
        textVersion: z.string().trim().min(1).max(64).optional(),
    }),
    /** Cloudflare Turnstile token (verified server-side). */
    turnstileToken: z.string().min(1).max(4096),
    calculator: z.object({
        input: calculatorInputSchema,
        result: calculatorResultSchema,
    }),
    /**
     * Attribution. `source` is trimmed and length-bounded; `utm` keeps only the
     * known keys, with cleaned values (schemas/attribution.ts, RV616 m5). Not
     * PII-free by construction: utm stays personal data under the privacy text.
     */
    source: z.string().trim().max(120).optional(),
    utm: attributionSchema,
    /**
     * Honeypot (Fase A6, added in the #491 fix round — MAJOR-3). The Zinsrechner
     * mints a REAL lead on a public host, so it gets the same edge gates as the
     * Bewertungs-funnel; accepting the key here is what lets the edge answer a
     * filled honeypot with an ordinary 200 instead of a schema 400 that would
     * name the trap. The backend maps its lead fields explicitly and never reads
     * this one.
     */
    [HONEYPOT_FIELD_NAME]: honeypotFieldSchema,
});
/** Internal lead route (POST /internal/lead) — proxied from WP via n8n. */
export const internalLeadSchema = leadIngestSchema.omit({ turnstileToken: true }).extend({
    /** Origin marker so we can attribute WP-form leads. */
    origin: z.literal('wordpress'),
});
